import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  User,
  Eye,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const statusColors = {
  scheduled: 'bg-sky-100 text-sky-700 border-sky-200',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function SiteVisitCalendar({ visits = [], onVisitClick, onDateSelect, loading = false }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week, day
  
  const getMonthDays = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    
    // Add days from previous month
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = new Date(year, month, -i);
      days.push({ date: day, isCurrentMonth: false });
    }
    
    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Add days from next month to complete grid
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  }, [currentDate]);

  const getWeekDays = useCallback(() => {
    const days = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push({ date: day, isCurrentMonth: day.getMonth() === currentDate.getMonth() });
    }
    return days;
  }, [currentDate]);

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getVisitsForDate = (date) => {
    const dateKey = formatDateKey(date);
    return visits.filter(v => v.planned_date === dateKey);
  };

  const isToday = (date) => {
    const today = new Date();
    return formatDateKey(date) === formatDateKey(today);
  };

  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(currentDate.getMonth() + direction);
    } else if (viewMode === 'week') {
      newDate.setDate(currentDate.getDate() + (direction * 7));
    } else {
      newDate.setDate(currentDate.getDate() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const days = viewMode === 'week' ? getWeekDays() : getMonthDays();

  const renderDayCell = (dayInfo, index) => {
    const dayVisits = getVisitsForDate(dayInfo.date);
    const maxVisitsToShow = viewMode === 'week' ? 5 : 3;
    const hasMore = dayVisits.length > maxVisitsToShow;
    
    return (
      <div
        key={index}
        className={cn(
          'min-h-[100px] border-r border-b border-slate-100 p-1 cursor-pointer transition-colors',
          !dayInfo.isCurrentMonth && 'bg-slate-50/50',
          isToday(dayInfo.date) && 'bg-sky-50/50',
          'hover:bg-slate-50'
        )}
        onClick={() => onDateSelect && onDateSelect(dayInfo.date)}
        data-testid={`calendar-day-${formatDateKey(dayInfo.date)}`}
      >
        <div className={cn(
          'text-sm font-medium mb-1',
          !dayInfo.isCurrentMonth && 'text-slate-400',
          isToday(dayInfo.date) && 'text-sky-600'
        )}>
          <span className={cn(
            'inline-flex items-center justify-center w-6 h-6 rounded-full',
            isToday(dayInfo.date) && 'bg-sky-500 text-white'
          )}>
            {dayInfo.date.getDate()}
          </span>
        </div>
        
        <div className="space-y-0.5">
          {dayVisits.slice(0, maxVisitsToShow).map((visit) => (
            <TooltipProvider key={visit.id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      onVisitClick && onVisitClick(visit);
                    }}
                    className={cn(
                      'text-xs px-1.5 py-0.5 rounded truncate cursor-pointer border',
                      statusColors[visit.status] || 'bg-slate-100 text-slate-600'
                    )}
                    style={{ 
                      borderLeftColor: visit.color || '#0ea5e9',
                      borderLeftWidth: '3px'
                    }}
                    data-testid={`calendar-visit-${visit.id}`}
                  >
                    <span className="font-medium">{visit.planned_time || '09:00'}</span> {visit.visit_type?.slice(0, 15)}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <div className="space-y-1">
                    <p className="font-semibold">{visit.visit_number}</p>
                    <p className="text-sm">{visit.visit_type}</p>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <MapPin className="w-3 h-3" />
                      {visit.site}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Clock className="w-3 h-3" />
                      {visit.planned_time || '09:00'} • {visit.planned_duration}
                    </div>
                    {visit.assigned_technician_name && (
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <User className="w-3 h-3" />
                        {visit.assigned_technician_name}
                      </div>
                    )}
                    <Badge variant="outline" className={cn('text-xs', statusColors[visit.status])}>
                      {visit.status}
                    </Badge>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
          {hasMore && (
            <div className="text-xs text-slate-500 pl-1">
              +{dayVisits.length - maxVisitsToShow} more
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayVisits = getVisitsForDate(currentDate);
    const hours = Array.from({ length: 14 }, (_, i) => i + 6); // 6 AM to 8 PM

    return (
      <div className="overflow-auto">
        <div className="text-center py-2 border-b font-medium">
          {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
        <div className="divide-y">
          {hours.map(hour => {
            const hourVisits = dayVisits.filter(v => {
              const visitHour = parseInt((v.planned_time || '09:00').split(':')[0]);
              return visitHour === hour;
            });
            
            return (
              <div key={hour} className="flex min-h-[60px]">
                <div className="w-16 text-xs text-slate-500 p-2 border-r bg-slate-50">
                  {hour.toString().padStart(2, '0')}:00
                </div>
                <div className="flex-1 p-1 space-y-1">
                  {hourVisits.map(visit => (
                    <div
                      key={visit.id}
                      onClick={() => onVisitClick && onVisitClick(visit)}
                      className={cn(
                        'p-2 rounded border-l-4 cursor-pointer hover:bg-slate-50',
                        statusColors[visit.status]
                      )}
                      style={{ borderLeftColor: visit.color || '#0ea5e9' }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{visit.visit_number}</span>
                        <Badge variant="outline" className="text-xs">{visit.status}</Badge>
                      </div>
                      <p className="text-sm">{visit.visit_type}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{visit.site}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />{visit.planned_duration}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-sky-500" />
              {viewMode === 'day' 
                ? currentDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                : viewMode === 'week'
                ? `Week of ${getWeekDays()[0].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`
              }
            </CardTitle>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToToday} data-testid="calendar-today">
              Today
            </Button>
            
            <div className="flex items-center border rounded-md">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)} data-testid="calendar-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(1)} data-testid="calendar-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger className="w-[110px]" data-testid="calendar-view-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="week">Week</SelectItem>
                <SelectItem value="day">Day</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
          </div>
        ) : viewMode === 'day' ? (
          renderDayView()
        ) : (
          <>
            {/* Day headers */}
            <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
              {DAYS.map(day => (
                <div key={day} className="py-2 text-center text-sm font-medium text-slate-600 border-r last:border-r-0">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className={cn(
              'grid grid-cols-7',
              viewMode === 'week' ? '' : 'grid-rows-6'
            )}>
              {days.slice(0, viewMode === 'week' ? 7 : 42).map((dayInfo, index) => renderDayCell(dayInfo, index))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
